import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JoinUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    year: '',
    department: '',
    why_join: '',
    experience: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/join-us`, formData);
      toast.success('Application submitted successfully! We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        year: '',
        department: '',
        why_join: '',
        experience: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20" data-testid="join-us-page">
      <section className="section-padding bg-gradient-to-b from-black to-[#121212]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-gold-500 mb-4">BECOME A MEMBER</p>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tighter uppercase mb-6" style={{ fontFamily: 'Open Sans' }}>JOIN KAROBAR</h1>
            <p className="text-gray-400 text-lg">Take the first step towards your entrepreneurial journey. Fill out the form below and become part of our growing community.</p>
          </div>

          <div className="glass-card p-8 lg:p-12">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="join-us-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-black/50 border border-white/10 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 text-white px-4 py-3 outline-none transition-all"
                    placeholder="Enter your full name"
                    data-testid="input-name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-black/50 border border-white/10 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 text-white px-4 py-3 outline-none transition-all"
                    placeholder="your.email@christuniversity.in"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-300 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full bg-black/50 border border-white/10 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 text-white px-4 py-3 outline-none transition-all"
                    placeholder="+91 XXXXXXXXXX"
                    data-testid="input-phone"
                  />
                </div>

                <div>
                  <label htmlFor="year" className="block text-sm font-semibold text-gray-300 mb-2">Year of Study *</label>
                  <select
                    id="year"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    required
                    className="w-full bg-black/50 border border-white/10 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 text-white px-4 py-3 outline-none transition-all"
                    data-testid="select-year"
                  >
                    <option value="">Select Year</option>
                    <option value="1st Year">1st Year</option>
                    <option value="2nd Year">2nd Year</option>
                    <option value="3rd Year">3rd Year</option>
                    <option value="4th Year">4th Year</option>
                    <option value="Postgraduate">Postgraduate</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="department" className="block text-sm font-semibold text-gray-300 mb-2">Department/Program *</label>
                <input
                  type="text"
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  required
                  className="w-full bg-black/50 border border-white/10 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 text-white px-4 py-3 outline-none transition-all"
                  placeholder="e.g., BBA, B.Tech, MBA"
                  data-testid="input-department"
                />
              </div>

              <div>
                <label htmlFor="why_join" className="block text-sm font-semibold text-gray-300 mb-2">Why do you want to join KAROBAR? *</label>
                <textarea
                  id="why_join"
                  name="why_join"
                  value={formData.why_join}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full bg-black/50 border border-white/10 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 text-white px-4 py-3 outline-none transition-all resize-none"
                  placeholder="Tell us about your interest in entrepreneurship and what you hope to gain from KAROBAR..."
                  data-testid="textarea-why-join"
                />
              </div>

              <div>
                <label htmlFor="experience" className="block text-sm font-semibold text-gray-300 mb-2">Previous Experience (Optional)</label>
                <textarea
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  rows={3}
                  className="w-full bg-black/50 border border-white/10 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 text-white px-4 py-3 outline-none transition-all resize-none"
                  placeholder="Any previous startup experience, projects, or relevant activities..."
                  data-testid="textarea-experience"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-application-button"
              >
                {isSubmitting ? 'SUBMITTING...' : 'SUBMIT APPLICATION'}
              </button>
            </form>
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-400">Have questions? Reach out to us at <a href="mailto:karobar.dps@christuniversity.in" className="text-gold-500 hover:underline">karobar.dps@christuniversity.in</a></p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JoinUs;